import { Component, DestroyRef, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { merge } from 'rxjs';
import { TranslocoModule } from '@ngneat/transloco';
import { CardsAdminService, DropSettings } from '../../../services/admin/cards-admin.service';

@Component({
  selector: 'app-cards-drops-tab',
  imports: [TranslocoModule, ReactiveFormsModule],
  templateUrl: './cards-drops-tab.component.html',
  styleUrl: './cards-drops-tab.component.scss'
})
export class CardsDropsTabComponent implements OnInit {
  questChanceControl = new FormControl<number>(0, { nonNullable: true });
  dailyQuestChanceControl = new FormControl<number>(0, { nonNullable: true });
  placeChanceControl = new FormControl<number>(0, { nonNullable: true });
  weightCommonControl = new FormControl<number>(0, { nonNullable: true });
  weightRareControl = new FormControl<number>(0, { nonNullable: true });
  weightLegendaryControl = new FormControl<number>(0, { nonNullable: true });
  ignoreWeeklyCooldownControl = new FormControl<boolean>(false, { nonNullable: true });
  ignoreDailyCapControl = new FormControl<boolean>(false, { nonNullable: true });

  loading = signal(true);
  // True only when the initial GET failed - distinct from `loading` so the template can tell
  // "still loading" apart from "loading finished, but there is nothing valid to show". Gates the
  // form: an admin must never see (and be able to type over/save) the all-zero control defaults
  // believing they're the live configuration.
  loadFailed = signal(false);
  saving = signal(false);
  saved = signal(false);
  error = signal<string | null>(null);

  // Bumped by the valueChanges subscription below on every real edit (and, incidentally, by the
  // programmatic setValue calls in patchControls - harmless, since it's only ever compared against
  // a snapshot taken immediately before a specific save() call, not used as a running total).
  // save() captures the current value before POSTing; if it no longer matches when the response
  // lands, the admin changed a field while the request was in flight, so the response is a stale
  // snapshot - applying it would silently overwrite their newer edit and the "Saved" banner would
  // lie about what's actually on screen.
  private editSeq = 0;

  constructor(
    private cardsAdmin: CardsAdminService,
    private destroyRef: DestroyRef
  ) {
    // Any edit invalidates the previous "Saved" confirmation / stale error message.
    merge(
      this.questChanceControl.valueChanges,
      this.dailyQuestChanceControl.valueChanges,
      this.placeChanceControl.valueChanges,
      this.weightCommonControl.valueChanges,
      this.weightRareControl.valueChanges,
      this.weightLegendaryControl.valueChanges,
      this.ignoreWeeklyCooldownControl.valueChanges,
      this.ignoreDailyCapControl.valueChanges
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.editSeq++;
        this.saved.set(false);
        this.error.set(null);
      });
  }

  ngOnInit(): void {
    this.cardsAdmin.getDropSettings$().subscribe({
      next: (s) => {
        this.patchControls(s);
        this.loadFailed.set(false);
        this.loading.set(false);
      },
      error: () => {
        // Without this, a failed GET (401/403 in particular get no snackbar at all from the
        // global interceptor) leaves `loading` true forever - the tab is stuck on "Loading..."
        // with no way out short of switching tabs and back. `loadFailed` (rather than reusing
        // `error`, which also carries save-time feedback) keeps the form hidden in this state -
        // the controls never got a real value, so rendering them would let an admin type plausible
        // numbers into what looks like the current config and save over the real settings.
        this.loading.set(false);
        this.loadFailed.set(true);
      }
    });
  }

  save(): void {
    if (this.saving()) {
      return;
    }
    const settings: DropSettings = {
      questChance: this.questChanceControl.value,
      dailyQuestChance: this.dailyQuestChanceControl.value,
      placeChance: this.placeChanceControl.value,
      weightCommon: this.weightCommonControl.value,
      weightRare: this.weightRareControl.value,
      weightLegendary: this.weightLegendaryControl.value,
      ignoreWeeklyCooldown: this.ignoreWeeklyCooldownControl.value,
      ignoreDailyCap: this.ignoreDailyCapControl.value
    };
    if (!this.isValid(settings)) {
      this.saved.set(false);
      this.error.set('cardsAdminDropInvalid');
      return;
    }
    const seq = this.editSeq;
    this.saving.set(true);
    this.error.set(null);
    this.cardsAdmin.saveDropSettings$(settings).subscribe({
      next: (s) => {
        // Skip the patch (and the "Saved" confirmation) if the admin edited a field after this
        // request went out - `s` is an echo of the OLDER values this request sent, not of
        // whatever is on screen now, so applying it would clobber their newer, unsaved edit.
        if (seq === this.editSeq) {
          this.patchControls(s);
          this.saved.set(true);
        }
        this.saving.set(false);
      },
      error: () => {
        // No snackbar for 401/403 from the global interceptor, so without this the button just
        // re-enables silently and the admin has no way to tell the save was rejected rather than
        // applied - they'd reasonably assume it went through.
        this.saved.set(false);
        this.error.set('cardsAdminDropSaveFailed');
        this.saving.set(false);
      }
    });
  }

  // Mirrors the server-side validation in the drop-settings endpoint (invalidChance / invalidWeights)
  // so obviously-bad input never round-trips to the API.
  private isValid(s: DropSettings): boolean {
    const chances = [s.questChance, s.dailyQuestChance, s.placeChance];
    if (chances.some((c) => !Number.isFinite(c) || c < 0 || c > 1)) {
      return false;
    }
    const weights = [s.weightCommon, s.weightRare, s.weightLegendary];
    if (weights.some((w) => !Number.isInteger(w) || w < 0)) {
      return false;
    }
    if (weights.every((w) => w === 0)) {
      return false;
    }
    return true;
  }

  private patchControls(s: DropSettings): void {
    this.questChanceControl.setValue(s.questChance);
    this.dailyQuestChanceControl.setValue(s.dailyQuestChance);
    this.placeChanceControl.setValue(s.placeChance);
    this.weightCommonControl.setValue(s.weightCommon);
    this.weightRareControl.setValue(s.weightRare);
    this.weightLegendaryControl.setValue(s.weightLegendary);
    this.ignoreWeeklyCooldownControl.setValue(s.ignoreWeeklyCooldown);
    this.ignoreDailyCapControl.setValue(s.ignoreDailyCap);
  }
}
